/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Library Player
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       26th October 2021

*******************************************************************************************/

import React                            from 'react';
import ReactJkMusicPlayer               from 'react-jinke-music-player'
import { CapacitorMusicControls }       from 'capacitor-music-controls-plugin-v3';
import { isEqual }                      from 'lodash';
import { 
    Box, useTheme, FormControl, 
    Select, MenuItem 
}                                       from '@mui/material';
import { RootContainer }                from 'components';
import { withTranslation }              from 'components/hoc';
import { 
    useModalSpacing,
    useDevice
}                                       from 'contexts';
import config                           from '../../config';
import cover                            from 'resources/logos/logo.png';

// Styling
import 'react-jinke-music-player/assets/index.css'  

const {
    company : COMPANY 
}                       = config;

const PLAYER_CONFIG = {
    defaultPlayIndex        : 0,
    playMode                : 'order', // order, orderLoop, singleLoop, shufflePlay
    mode                    : 'full',
    quietUpdate             : false,
    glassBg                 : true,
    responsive              : false,
    remember                : false,
    preload                 : true,
    once                    : false,
    autoPlay                : false,
    remove                  : false,
    toggleMode              : false,
    showDownload            : false,
    showReload              : false,
    showThemeSwitch         : false,
    showMediaSession        : true,
    showLyric               : false,
    autoHiddenCover         : true,
    restartCurrentOnPrev    : true,
    loadAudioErrorPlayNext  : false,
    clearPriorAudioLists    : true,
    showMiniProcessBar      : true,
    showMiniModeCover       : true,
    drag                    : false,
    spaceBar                : true,
    // autoPlayInitLoadPlayList: false,
    // locale: i18n.language, // https://github.com/lijinke666/react-music-player#bulb-internationalization
  };

const enforceArray      = x => Array.isArray(x) ? x : [x]
const noop              = () => {};
const arr               = [];
const DEFAULT_ZINDEX    = 1000000;

class BaseErrorBoundary extends React.Component {
    
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
  
    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }
  
    componentDidCatch(error, errorInfo) {
        // You can also log the error to an error reporting service
    }
  
    render() {
        const { t } = this.props;
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return (
                <h1>
                    { t('components.libraryJinkePlayer.errorBoundary.somethingWentWrong') }
                </h1>
            );
        }
        return this.props.children; 
    }
}

const ErrorBoundary = withTranslation(BaseErrorBoundary);

const ExtendsContent = ({
    playbackSpeed                       = 1,
    onSpeedChange : handleSpeedChange   = noop,
    zIndex                              = undefined
}) => {
    return (
        <FormControl key={'speed'}>
            <Select
                value       = {playbackSpeed}
                onChange    = {handleSpeedChange}
                color       = {'secondary'}
                MenuProps   = {{
                    ...(Number.isFinite(zIndex) && zIndex >= 0 && {
                        style: {
                            zIndex
                        }
                    })
                }}
            >
                {
                    [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((value) => (
                        <MenuItem key={value} value={value}>
                            {value}x
                        </MenuItem>
                    ))
                }
            </Select>
        </FormControl>
    )
}


const useCapacitorMusicControls = ({
    audioInstance,
    audioLists,

    playIndex,
    playing,
    currentTime,
    duration,
  }) => {

    const {isNative}            = useDevice();
    const [isSetup, setIsSetup] = React.useState(false);

    React.useEffect(() => {

        // Early exit if not native or no valid track or audioInstance
        if (!isNative || !audioInstance) 
            return;

        // Define the controlsNotification handler function
        const controlsNotificationHandler = (event) => {
            const {message} = event;
            switch(message) {
                case 'music-controls-next':
                    audioInstance.next();
                    break;
                case 'music-controls-previous':
                    audioInstance.prev();
                    break;
                case 'music-controls-pause':
                    audioInstance.pause();
                    break;
                case 'music-controls-play':
                    audioInstance.play();
                    break;
                case 'music-controls-destroy':
                    // controls were destroyed
                    break;
        
                // External controls (iOS only)
                case 'music-controls-toggle-play-pause' :
                    // do something
                    break;
                case 'music-controls-skip-to':
                    // do something
                    break;
                case 'music-controls-skip-forward':
                    // Do something
                    break;
                case 'music-controls-skip-backward':
                    // Do something
                    break;
        
                // Headset events (Android only)
                // All media button events are listed below
                case 'music-controls-media-button' :
                    // Do something
                    break;
                case 'music-controls-headset-unplugged':
                    // Do something
                    break;
                case 'music-controls-headset-plugged':
                    // Do something
                    break;
                default:
                    break;
            }
        };

        // Only create CapacitorMusicControls once (setup)
        const setupControls = async () => {

            // Create CapacitorMusicControls with initial placeholder info
            await CapacitorMusicControls.create({
                track           : 'Unknown Track',
                artist          : 'Unknown Artist',
                cover           : cover,
                isPlaying       : false,
                dismissable     : false,
                hasPrev         : false,
                hasNext         : false,
                hasClose        : false,
                duration        : 0,
                elapsed         : 0,
            });

            // Add listener for control actions
            CapacitorMusicControls.addListener('controlsNotification', controlsNotificationHandler);

            // Declare Is Setup
            setIsSetup(true);
        };

        setupControls();

        // Cleanup function to remove listeners and destroy controls
        return () => {
            CapacitorMusicControls.removeListener('controlsNotification', controlsNotificationHandler);
            CapacitorMusicControls.destroy();
            setIsSetup(false);
        };

    }, [isNative, audioInstance, audioLists, playIndex]);

    // Dynamically update playing state and elapsed time
    React.useEffect(() => {
        if(!isNative || !isSetup || !audioInstance)
            return;

        // Update isPlaying dynamically
        CapacitorMusicControls.updateIsPlaying({ isPlaying: playing });

        // Update elapsed time dynamically
        if (!isNaN(currentTime)) {
            CapacitorMusicControls.updateElapsed({ elapsed: currentTime });
        }

    }, [isNative, isSetup, audioInstance, playing, currentTime]);
  
    // Optionally, update other control properties here as needed (e.g., track name, artist, duration)
    React.useEffect(() => {
        if(!isNative || !isSetup || !audioInstance)
            return;

        if(isNaN(playIndex) || !audioLists?.[playIndex])
            return;

        const   track = audioLists[playIndex],  
                hasPrev = playIndex > 0,                        // Check if there's a previous track
                hasNext = playIndex < audioLists.length - 1,    // Check if there's a next track
                elapsed = currentTime;

        // Update the CapacitorMusicControls dynamically
        CapacitorMusicControls.update({
            track       : track.name    || 'Unknown Track',
            artist      : track.singer  || 'Unknown Artist',
            cover       : track.cover   || '',
            duration,
            elapsed,
            hasPrev,                       
            hasNext    
            
        });

    }, [isNative, isSetup, audioInstance, audioLists, playIndex, duration, currentTime]);
}

// Library Jinke Player Custom Component
export const LibraryJinkePlayer = ({
    hidden                                                  = false,
    playList                                                = undefined,
    playListIndex           : playIndex                     = 0, 
    onAudioInstanceChange   : handleAudioInstanceChange     = noop,
    onAudioPlay             : handleAudioPlay               = noop,
    onAudioPause            : handleAudioPause              = noop,
    onAudioEnded            : handleAudioEnded              = noop,
    onAudioAbort            : handleAudioAbort              = noop,
    onPlayIndexChange       : handlePlayIndexChange         = noop,
    onAudioSeeked           : handleAudioSeeked             = noop,
    onAudioProgress         : handleAudioProgress           = noop,
    onAudioListChange       : handleAudioListChange         = noop,
    zIndex                                                  = DEFAULT_ZINDEX,
    sortable                                                = false,
    fadems                                                  = 0,
    ...props
}) => {
    const {isNative,safeAreaInsets}             = useDevice();
    const theme                                 = useTheme();
    const [ audioInstance, setAudioInstance ]   = React.useState(undefined);
    const [ playbackSpeed, setPlaybackSpeed ]   = React.useState(1);
    const [ audioInfo,     setAudioInfo]        = React.useState(undefined);
    const {
        top,
        bottom,
        setBottom,
        reset : resetModalSpacing,
        defaults : {
            bottom : defaultBottom
        }
    }                                           = useModalSpacing();
    const [audioList,   setAudioList]           = React.useState([]);

    React.useEffect(() => {
        const handler = function(e) {
            const elem = e.target.nodeName;
            if( elem !== 'TEXTAREA' && elem !== 'INPUT' ) { // Not Input
                if(e.keyCode === 32){ // SpaceBar
                    e.preventDefault();
                }
            }
        }
        window.addEventListener('keydown', handler );
        return () => {
            window.removeEventListener('keydown', handler);
        }
    },[])

    React.useEffect(() => {
        handleAudioInstanceChange(audioInstance);
    },[audioInstance, handleAudioInstanceChange])
    
    React.useEffect(() => {
        if(audioInstance){
            audioInstance.pause();
            return () => {
                audioInstance.pause();
            }
        }
    }, [audioInstance])

    // Helper function 
    const makeAudioList = React.useCallback( (playList) => (
        playList.map((data,ix) => {
            let name        = `File Number ${ix + 1}`,
                musicSrc    = data;
            let suffix      = name;
            if(typeof data === 'object'){
                let {file, description} = data;
                musicSrc    = file;
                suffix      = description || name;
            }
            return {
                musicSrc    : musicSrc, 
                singer      : COMPANY || window.location.origin, 
                name        : `${ix+1}. ${suffix}`,
                cover       : cover
            }
        })
    ), []);

    // Override Jinke Styles
    React.useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
            .react-jinke-music-player {
                right   : ${theme.spacing(4)};
                bottom  : ${theme.spacing(4)};
                left    : unset!important;
                top     : unset!important;
                z-index : ${zIndex}!important;
            }
            .audio-lists-panel.show {
                z-index : ${zIndex}!important;
            }
            .music-player-panel {
                display : ${hidden ? 'none' : 'initial'};
                z-index : ${zIndex}!important;
            }
            .audio-lists-panel-content .audio-item .player-singer {
                width   : 90px!important;
            }
            .react-jinke-music-player-main .music-player-panel .panel-content .img-rotate {
                animation: none !important;
                transform: none !important;
            }
        `
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        }
    },[theme, hidden, zIndex, sortable])

    React.useEffect(() => {
        if(isNative){
            const style = document.createElement('style')
            style.innerHTML = `
                .audio-lists-panel.show {
                    margin-top      : ${safeAreaInsets.top}px;
                }
            `
            document.head.appendChild(style);
            return () => {
                document.head.removeChild(style);
            }
        }
    },[isNative,safeAreaInsets])

     // Use Lodash's _.isEqual for deep comparison of arrays of objects
    const hasPlayListChanged = React.useCallback( (prevPlayList, newPlayList) => !isEqual(prevPlayList, newPlayList), []);

    // Set the AudioList once Layout is Complete
    const timeoutRef        = React.useRef(null);
    const prevPlayListRef   = React.useRef(null);
    React.useEffect(() => {
        clearTimeout(timeoutRef.current);
        if(audioInstance && hasPlayListChanged(prevPlayListRef.current, playList)){

            // Update Audio List Function
            const updateAudioList = () => {
                const newAudioList = makeAudioList( enforceArray(playList) );
                setAudioList( newAudioList );
            }

            // SET AUDIO LIST ON SLIGHT DELAY TO PREVENT AUTOPLAY
            setAudioList(arr); // Set to Empty
            timeoutRef.current = setTimeout(updateAudioList,1000)

            // Update the ref with the current playList after comparison
            prevPlayListRef.current = playList;
        }
        // Cleanup
        return () => {
            clearTimeout(timeoutRef.current);
            setAudioList(arr); // Clear Audio List on Cleanup
        }
    
    }, [audioInstance, hasPlayListChanged, makeAudioList, playList])
    
    // Make Spacing
    React.useEffect(() => {
        if(hidden) 
            return;
        setBottom(defaultBottom + 80);
        return () => {
            resetModalSpacing();
        };

    },[defaultBottom, hidden, resetModalSpacing, setBottom]);

    const handleSpeedChange = React.useCallback( (event) => {
        setPlaybackSpeed(
            parseFloat(event.target.value)
        );
    }, []);

    React.useEffect(() => {
        if(!audioInstance || !playbackSpeed)
            return;
        audioInstance.playbackRate = playbackSpeed;
    },[playbackSpeed, audioInstance]);

    // Intercept Audio Info
    const interceptInfo = React.useCallback( (fn) => (
        (info, ...args) => {
            setAudioInfo(info);     // Store latest audioInfo
            fn?.(info, ...args);    // Forward to original handler
        }
    ), []);
    
    // Use Music Controls Hook
    useCapacitorMusicControls({
        audioInstance,
        audioList,
        playIndex,
        playing     : audioInfo?.playing        || false,
        currentTime : audioInfo?.currentTime    || 0,
        duration    : audioInfo?.duration       || 0,
    })

    return (
        <RootContainer>
            <ErrorBoundary>
                <ReactJkMusicPlayer 
                    extendsContent              = { 
                        <ExtendsContent 
                            playbackSpeed   = { playbackSpeed } 
                            onSpeedChange   = { handleSpeedChange } 
                            zIndex          = { zIndex + 1 }
                        /> 
                    }
                    theme                       = { theme.palette.mode }
                    sortableOptions             = {{ 
                        disabled : !Boolean(sortable) 
                    }}
                    volumeFade                  = {{
                        fadeIn  : fadems, 
                        fadeOut : fadems
                    }}
                    {...PLAYER_CONFIG}
                    {...props}
                    audioLists                  = { audioList }
                    playIndex                   = { playIndex }

                    onAudioPlay                 = { interceptInfo( handleAudioPlay ) }
                    onAudioPause                = { interceptInfo( handleAudioPause ) }
                    onAudioSeeked               = { interceptInfo( handleAudioSeeked ) }
                    onAudioProgress             = { interceptInfo( handleAudioProgress )} 

                    onAudioEnded                = { handleAudioEnded }
                    onAudioAbort                = { handleAudioAbort }
                    onPlayIndexChange           = { handlePlayIndexChange }
                    onAudioListsChange          = { handleAudioListChange }
                    getAudioInstance            = { setAudioInstance }
                />

                {   
                    false &&
                    <Box position={"fixed"} style={{top:200,left:0,background:'red'}}>
                        {top} {bottom} {hidden ? "hidden" : "not hidden"}
                    </Box>
                }
            </ErrorBoundary>
        </RootContainer>
    )
}

export default LibraryJinkePlayer;